/**
 * /admin/announcements — what the team sees, and who decides.
 *
 * Announcements used to live in the dashboard's source, so publishing
 * one was a side effect of a deploy: it went in front of every user the
 * moment a release landed, with no approval and no way to stop it
 * without shipping again.
 *
 * This screen is the gate. Everything starts as a draft, publishing is a
 * deliberate button, and unpublishing takes it back immediately — no
 * deploy in either direction.
 *
 * ── Reading the layout ────────────────────────────────────────────────
 * A live announcement is in front of the whole team right now, so it
 * does not look like a row in a list: it carries a gold rail and a warm
 * card, while drafts sit quiet and unfilled. You should be able to tell
 * what is currently interrupting people from across the room.
 */

import AddIcon from '@mui/icons-material/Add';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import {
  useAdminAnnouncements,
  useDeleteAnnouncement,
  useSetAnnouncementStatus,
  type AdminAnnouncement,
  type Announcement,
} from '../../api/announcements';
import { colors, docLeading, uiType } from '../../theme';
import { AnnouncementDialog } from './AnnouncementDialog';
import { AnnouncementPreview } from './AnnouncementPreview';
import { AnnouncementReadersDrawer } from './AnnouncementReadersDrawer';

const GOLD = colors.goldDark;
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';
const SERIF = '"Playfair Display", Georgia, serif';

/** `2026-09-02` → `2 Sep 2026`. Dashes read as machine output. */
function prettyDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function StatusPill({ status }: { status: AdminAnnouncement['status'] }) {
  const live = status === 'published';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.55,
        px: 0.9,
        py: 0.28,
        borderRadius: 999,
        fontFamily: FONT_INTER,
        fontSize: uiType.micro,
        fontWeight: 800,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: live ? colors.success : colors.textMuted,
        bgcolor: live ? `${colors.success}14` : 'rgba(15,10,7,0.05)',
        border: `1px solid ${live ? `${colors.success}3d` : 'rgba(15,10,7,0.10)'}`,
      }}
    >
      {/* A live announcement gets a lit dot — the same signal the header
          uses for an active session. */}
      {live && (
        <Box
          aria-hidden
          sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: colors.success }}
        />
      )}
      {live ? 'Live' : 'Draft'}
    </Box>
  );
}

function FormatPill({ presentation }: { presentation: AdminAnnouncement['presentation'] }) {
  return (
    <Box
      component="span"
      sx={{
        px: 0.9,
        py: 0.28,
        borderRadius: 999,
        fontFamily: FONT_INTER,
        fontSize: uiType.micro,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: colors.textSecondary,
        bgcolor: 'rgba(15,10,7,0.04)',
        border: '1px solid rgba(15,10,7,0.08)',
      }}
    >
      {presentation === 'modal' ? 'Popup' : 'Banner'}
    </Box>
  );
}

/**
 * How many have seen it, out of everyone who could — and the way into
 * the names.
 *
 * The number alone cannot say whether the announcement worked: someone
 * who read it and someone who swatted it away both add one. Clicking
 * through is where that gets answered.
 */
function Reach({
  seen,
  audience,
  onClick,
}: {
  seen: number;
  audience: number;
  onClick: () => void;
}) {
  const pct = audience > 0 ? Math.round((seen / audience) * 100) : 0;
  return (
    <Tooltip title="See who has and has not seen this" arrow>
      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          width: 132,
          flexShrink: 0,
          textAlign: 'left',
          border: '1px solid transparent',
          bgcolor: 'transparent',
          px: 1.25,
          py: 0.9,
          borderRadius: 1.5,
          cursor: 'pointer',
          font: 'inherit',
          transition: 'background-color 160ms ease, border-color 160ms ease',
          '&:hover': { bgcolor: `${GOLD}0d`, borderColor: `${GOLD}2e` },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography
            sx={{
              fontFamily: SERIF,
              fontSize: uiType.headline,
              fontWeight: 600,
              color: colors.textPrimary,
            }}
          >
            {seen}
          </Typography>
          <Typography
            sx={{ fontFamily: FONT_INTER, fontSize: uiType.caption, color: colors.textMuted }}
          >
            of {audience} seen
          </Typography>
        </Box>
        <Box sx={{ height: 5, borderRadius: 3, bgcolor: 'rgba(15,10,7,0.07)', mt: 0.6 }}>
          <Box
            aria-hidden
            sx={{
              width: `${pct}%`,
              height: '100%',
              borderRadius: 3,
              background: `linear-gradient(to right, ${colors.gold}, ${GOLD})`,
              transition: 'width 300ms ease',
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
}

/** Edit / delete — quiet until you reach for them. */
/** An admin row as a reader would receive it. */
function toPreview(a: AdminAnnouncement): Announcement {
  return {
    key: `preview-${a.id}`,
    title: a.title,
    body: a.body,
    presentation: a.presentation,
    mark: a.mark,
    action: a.action_label && a.action_to ? { label: a.action_label, to: a.action_to } : null,
  };
}

function RowAction({
  title,
  disabled,
  onClick,
  children,
}: {
  title: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={onClick}
          sx={{
            color: colors.textMuted,
            transition: 'color 160ms ease, background-color 160ms ease',
            '&:hover': { color: GOLD, bgcolor: `${GOLD}12` },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export function AnnouncementsPage() {
  const query = useAdminAnnouncements();
  const setStatus = useSetAnnouncementStatus();
  const remove = useDeleteAnnouncement();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminAnnouncement | null>(null);
  // Which row is mid-write, so only its own button shows a spinner.
  const [busyId, setBusyId] = useState<number | null>(null);
  const [readersFor, setReadersFor] = useState<AdminAnnouncement | null>(null);
  const [previewing, setPreviewing] = useState<AdminAnnouncement | null>(null);

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (a: AdminAnnouncement) => {
    setEditing(a);
    setDialogOpen(true);
  };

  const togglePublish = (a: AdminAnnouncement) => {
    setBusyId(a.id);
    setStatus.mutate(
      { id: a.id, status: a.status === 'published' ? 'draft' : 'published' },
      { onSettled: () => setBusyId(null) },
    );
  };

  const items = query.data?.items ?? [];
  const audience = query.data?.audience ?? 0;
  const liveCount = items.filter((a) => a.status === 'published').length;

  return (
    <Stack gap={3}>
      {/* ---- Header ---- */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={2}
        sx={{ alignItems: { sm: 'flex-start' }, justifyContent: 'space-between' }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: uiType.micro,
              fontWeight: 700,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: GOLD,
              mb: 0.75,
            }}
          >
            Communications
          </Typography>
          <Typography
            sx={{
              fontFamily: SERIF,
              fontSize: { xs: uiType.headline, sm: uiType.display },
              fontWeight: 600,
              color: colors.textPrimary,
              lineHeight: docLeading.tight,
              letterSpacing: '-0.01em',
            }}
          >
            Announcements
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: uiType.body,
              color: colors.textSecondary,
              lineHeight: docLeading.normal,
              mt: 1,
              maxWidth: 560,
            }}
          >
            What the team sees when they sign in. Nothing here reaches anyone until you publish it,
            and unpublishing takes it back straight away.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          onClick={openNew}
          sx={{
            flexShrink: 0,
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: uiType.bodySm,
            px: 2,
            py: 1,
            borderRadius: 1.5,
            bgcolor: GOLD,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(139, 101, 38, 0.24)',
            transition: 'transform 160ms ease, box-shadow 160ms ease',
            '&:hover': {
              bgcolor: '#a67c34',
              boxShadow: '0 4px 14px rgba(139, 101, 38, 0.30)',
              transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0)' },
          }}
        >
          New Announcement
        </Button>
      </Stack>

      {/* One live announcement is the design; more than one means only the
          newest is ever seen, which is worth saying out loud rather than
          leaving an admin to wonder why theirs never appeared.

          Styled warm rather than using MUI's blue info Alert, which reads
          as a system error on a cream page. */}
      {liveCount > 1 && (
        <Stack
          direction="row"
          gap={1.25}
          sx={{
            alignItems: 'flex-start',
            px: 2,
            py: 1.5,
            borderRadius: 2,
            bgcolor: `${GOLD}0a`,
            border: `1px solid ${GOLD}2e`,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 17, color: GOLD, mt: '1px', flexShrink: 0 }} />
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: uiType.bodySm,
              color: colors.textSecondary,
              lineHeight: docLeading.normal,
            }}
          >
            <Box component="span" sx={{ fontWeight: 700, color: colors.textPrimary }}>
              {liveCount} announcements are live.
            </Box>{' '}
            Each person only ever sees the newest one they have not closed — the others wait their
            turn.
          </Typography>
        </Stack>
      )}

      {query.isError && <Alert severity="error">{(query.error as Error).message}</Alert>}
      {remove.isError && <Alert severity="error">{(remove.error as Error).message}</Alert>}
      {setStatus.isError && <Alert severity="error">{(setStatus.error as Error).message}</Alert>}

      {query.isPending && (
        <Stack gap={1.5}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={112} />
          ))}
        </Stack>
      )}

      {!query.isPending && items.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 9,
            px: 3,
            border: `1px dashed ${GOLD}3d`,
            borderRadius: 3,
            bgcolor: `${GOLD}06`,
          }}
        >
          <CampaignOutlinedIcon sx={{ fontSize: 34, color: GOLD, opacity: 0.65 }} />
          <Typography
            sx={{
              fontFamily: SERIF,
              fontSize: uiType.title,
              fontWeight: 600,
              mt: 1.5,
              color: colors.textPrimary,
            }}
          >
            Nothing to Announce Yet
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: uiType.body,
              color: colors.textSecondary,
              mt: 0.75,
              maxWidth: 380,
              mx: 'auto',
              lineHeight: docLeading.normal,
            }}
          >
            Write one when you ship something the team needs to know about. It saves as a draft, so
            you can look at it before anyone else does.
          </Typography>
        </Box>
      )}

      {/* ---- The list ---- */}
      <Stack gap={1.75}>
        {items.map((a) => {
          const live = a.status === 'published';
          const busy = busyId === a.id;
          return (
            <Box
              key={a.id}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 1.5, md: 2.5 },
                alignItems: { md: 'center' },
                p: { xs: 2, md: 2.5 },
                pl: { md: 3 },
                borderRadius: 2.5,
                position: 'relative',
                overflow: 'hidden',
                bgcolor: '#fff',
                border: `1px solid ${live ? `${GOLD}33` : colors.borderLight}`,
                boxShadow: live
                  ? '0 6px 20px -14px rgba(139, 101, 38, 0.45)'
                  : '0 1px 2px rgba(15,10,7,0.03)',
                transition: 'box-shadow 200ms ease, border-color 200ms ease',
                '&:hover': {
                  borderColor: live ? `${GOLD}5c` : 'rgba(15,10,7,0.16)',
                  boxShadow: live
                    ? '0 10px 26px -14px rgba(139, 101, 38, 0.55)'
                    : '0 3px 10px -6px rgba(15,10,7,0.12)',
                },
                // The rail. Only a live announcement gets one — it is
                // the thing in front of the team right now.
                '&::before': live
                  ? {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 3,
                      background: `linear-gradient(to bottom, ${colors.gold}, ${GOLD})`,
                    }
                  : undefined,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack
                  direction="row"
                  gap={0.75}
                  sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.6 }}
                >
                  <Typography
                    sx={{
                      fontFamily: FONT_INTER,
                      fontSize: uiType.body,
                      fontWeight: 700,
                      color: colors.textPrimary,
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {a.title}
                  </Typography>
                  <StatusPill status={a.status} />
                  <FormatPill presentation={a.presentation} />
                </Stack>

                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: uiType.bodySm,
                    color: colors.textSecondary,
                    lineHeight: docLeading.normal,
                    maxWidth: 720,
                  }}
                >
                  {a.body}
                </Typography>

                <Stack
                  direction="row"
                  gap={1}
                  sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 1 }}
                >
                  <Typography
                    sx={{
                      fontFamily: FONT_INTER,
                      fontSize: uiType.caption,
                      color: colors.textMuted,
                    }}
                  >
                    {live && a.published_on
                      ? `Live since ${prettyDate(a.published_on)}`
                      : 'Not published'}
                    {a.expires_on ? ` · retires ${prettyDate(a.expires_on)}` : ''}
                  </Typography>
                  {a.action_label && (
                    <Box
                      component="span"
                      sx={{
                        px: 0.85,
                        py: 0.2,
                        borderRadius: 1,
                        fontFamily: FONT_INTER,
                        fontSize: uiType.micro,
                        fontWeight: 600,
                        color: GOLD,
                        bgcolor: `${GOLD}12`,
                      }}
                    >
                      {a.action_label} →
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Reach and the controls travel together below the text
                  until there is room to sit beside it. `display: contents`
                  dissolves this wrapper at md so its children become
                  direct children of the card's row — three layouts, one
                  set of markup:

                    phone  — everything stacked
                    tablet — text on top, reach and buttons on one row
                    desktop— all three side by side */}
              <Box
                sx={{
                  display: { xs: 'flex', md: 'contents' },
                  width: { xs: '100%', md: 'auto' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                {live && (
                  <Reach seen={a.ack_count} audience={audience} onClick={() => setReadersFor(a)} />
                )}

                <Stack
                  direction="row"
                  gap={0.5}
                  sx={{ alignItems: 'center', flexShrink: 0, ml: { xs: 'auto', md: 0 } }}
                >
                  <RowAction title="Preview" onClick={() => setPreviewing(a)}>
                    <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                  </RowAction>

                  <RowAction title="Edit" onClick={() => openEdit(a)}>
                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  </RowAction>

                  {/* Deleting a published announcement would orphan the
                    record of who saw it, so the backend refuses. Disable
                    it here too rather than letting an admin discover the
                    rule through a red error. */}
                  <RowAction
                    title={live ? 'Unpublish before deleting' : 'Delete draft'}
                    disabled={live || remove.isPending}
                    onClick={() => remove.mutate(a.id)}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </RowAction>

                  <Button
                    size="small"
                    variant={live ? 'outlined' : 'contained'}
                    disabled={busy}
                    onClick={() => togglePublish(a)}
                    startIcon={
                      busy ? (
                        <CircularProgress size={13} thickness={5} />
                      ) : live ? (
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <CampaignOutlinedIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    sx={{
                      ml: 0.5,
                      fontFamily: FONT_INTER,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: uiType.caption,
                      px: 1.75,
                      py: 0.7,
                      borderRadius: 1.5,
                      whiteSpace: 'nowrap',
                      transition: 'all 160ms ease',
                      ...(live
                        ? {
                            color: colors.textSecondary,
                            borderColor: 'rgba(15,10,7,0.14)',
                            '&:hover': {
                              borderColor: GOLD,
                              color: GOLD,
                              bgcolor: `${GOLD}0a`,
                            },
                          }
                        : {
                            bgcolor: GOLD,
                            boxShadow: '0 2px 8px rgba(139, 101, 38, 0.22)',
                            '&:hover': {
                              bgcolor: '#a67c34',
                              boxShadow: '0 4px 14px rgba(139, 101, 38, 0.28)',
                            },
                          }),
                    }}
                  >
                    {live ? 'Unpublish' : 'Publish'}
                  </Button>
                </Stack>
              </Box>
            </Box>
          );
        })}
      </Stack>

      <AnnouncementDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => setDialogOpen(false)}
      />
      <AnnouncementReadersDrawer announcement={readersFor} onClose={() => setReadersFor(null)} />
      <AnnouncementPreview
        announcement={previewing && toPreview(previewing)}
        onClose={() => setPreviewing(null)}
      />
    </Stack>
  );
}
